import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Log } from 'app/core/logging/log';
import { LocationAvailableModel } from 'app/models/location-available-model';
import { AppService } from 'app/services/app/app.service';
import { RestService } from 'app/services/rest/rest.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class LocationGuard implements CanActivateChild  {

    constructor(
        private appService: AppService,
        private router: Router,
        private restService: RestService
    ) { }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree {

        const selected = this.appService.clientSelected;
        const isSelected = this.appService.hasClientSelected();
        Log.debug('LocationGuard', {selected: selected, isSelected: isSelected});

        // evitar loop: permitir acceso a /admin base
        if (route.routeConfig?.path === '') {
            return true;
        }

        // si hay location: ok
        if (isSelected) {
            return true;
        }

        const allow = route.data['allowWithoutLocation'];
        if (allow) {
            return true;
        }

        return this.resolveLocationSelection(state.url);
    }

    private resolveLocationSelection(currentUrl: string): Observable<boolean | UrlTree> {
        return this.restService.findClientLocker().pipe(
            map((locations: LocationAvailableModel[]) => {
                if (Array.isArray(locations) && locations.length === 1) {
                    this.appService.clientSelected = locations[0];
                    Log.debug('LocationGuard auto-selected location', { location: locations[0] });
                    return true;
                }

                this.appService.clientSelected = null;
                return this.buildMissingLocationRedirect(currentUrl);
            }),
            catchError((error) => {
                Log.error('LocationGuard error loading locations', { error });
                this.appService.clientSelected = null;
                return of(this.buildMissingLocationRedirect(currentUrl));
            })
        );
    }

    private buildMissingLocationRedirect(currentUrl: string): UrlTree {
        const cleanRedirect = currentUrl.split('?')[0];

        return this.router.createUrlTree(['/admin'], {
            queryParams: {
                reason: 'no-location',
                redirect: cleanRedirect,
            }
        });
    }
}
